import type { IOidcSession, IOidcSessionRecord } from "~/utils/interfaces/oidc-interfaces";
import { isAuthLoggingOut } from "./lifecycle";
import { AUTH_REQUIRED_MESSAGE } from "./oidc-contract";

export const OIDC_SESSION_KEY = "teams.oidc.session.v1";
export const OIDC_SESSION_LOCK = "teams.oidc.session";
export const OIDC_RENEW_MARGIN_SECONDS = 60;
export type SessionLock = <T>(operation: () => Promise<T>) => Promise<T>;

export class OidcSessionCoordinator {
  constructor(
    private readonly storage: Storage,
    private readonly lock: SessionLock,
    private readonly now: () => number = () => Date.now() / 1000,
    private readonly finishRenewalOnLogout = false,
  ) {}

  record(): IOidcSessionRecord | null {
    try {
      const value = JSON.parse(this.storage.getItem(OIDC_SESSION_KEY) ?? "null") as IOidcSessionRecord | null;
      if (!value) return null;
      if (
        value.version !== 1 ||
        typeof value.generation !== "string" ||
        !Number.isInteger(value.revision) ||
        typeof value.renewing !== "boolean" ||
        (value.session && !this.valid(value.session))
      )
        throw new Error();
      return value;
    } catch {
      this.storage.removeItem(OIDC_SESSION_KEY);
      return null;
    }
  }

  read(): IOidcSession | null {
    return this.record()?.session ?? null;
  }

  private valid(value: IOidcSession): boolean {
    return (
      !!value &&
      [value.access_token, value.refresh_token, value.id_token, value.subject].every(
        (item) => typeof item === "string" && !!item,
      ) &&
      Number.isFinite(value.expires_at)
    );
  }

  private write(record: IOidcSessionRecord): void {
    this.storage.setItem(OIDC_SESSION_KEY, JSON.stringify(record));
  }

  establish(session: IOidcSession): void {
    if (!this.valid(session)) throw new Error(AUTH_REQUIRED_MESSAGE);
    this.write({ version: 1, generation: crypto.randomUUID(), revision: 0, renewing: false, session });
  }

  clear(generation: string = crypto.randomUUID()): string {
    this.write({ version: 1, generation, revision: 0, renewing: false, session: null });
    return generation;
  }

  async renew(
    exchange: (session: IOidcSession) => Promise<IOidcSession>,
    force = false,
    prepare?: () => Promise<void>,
  ): Promise<IOidcSession> {
    const requested = this.record();
    if (isAuthLoggingOut() || !requested?.session) throw new Error(AUTH_REQUIRED_MESSAGE);
    return this.lock(async () => {
      const current = this.record();
      if (isAuthLoggingOut() || !current?.session || current.generation !== requested.generation)
        throw new Error(AUTH_REQUIRED_MESSAGE);
      // An abandoned exchange might already have rotated this credential on the server.
      if (current.renewing) {
        this.clear();
        throw new Error(AUTH_REQUIRED_MESSAGE);
      }
      if (
        current.session.expires_at > this.now() + OIDC_RENEW_MARGIN_SECONDS &&
        (!force || current.revision !== requested.revision)
      )
        return current.session;
      if (prepare) await prepare();
      if (isAuthLoggingOut() || this.record()?.generation !== current.generation)
        throw new Error(AUTH_REQUIRED_MESSAGE);
      this.write({ ...current, renewing: true });
      try {
        const next = await exchange(current.session);
        if ((isAuthLoggingOut() && !this.finishRenewalOnLogout) || this.record()?.generation !== current.generation)
          throw new Error(AUTH_REQUIRED_MESSAGE);
        if (!this.valid(next) || next.expires_at <= this.now()) throw new Error(AUTH_REQUIRED_MESSAGE);
        this.write({ ...current, revision: current.revision + 1, renewing: false, session: next });
        return next;
      } catch {
        if (!isAuthLoggingOut() && this.record()?.generation === current.generation) this.clear();
        throw new Error(AUTH_REQUIRED_MESSAGE);
      }
    });
  }
}
